import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, ExternalLink, Eye, EyeOff, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { triggerGlobalRefresh } from "../../lib/autoRefresh";

// Settings > Mux Video. The secret is write-only: the API only returns a masked
// token id preview, and the form never shows a stored secret.
export default function MuxSettings() {
  const [loading, setLoading] = useState(false);
  const [muxTokenId, setMuxTokenId] = useState("");
  const [muxTokenSecret, setMuxTokenSecret] = useState("");
  const [showMuxSecret, setShowMuxSecret] = useState(false);
  const [muxConfig, setMuxConfig] = useState(null);
  const [status, setStatus] = useState(null);

  const loadMuxConfig = useCallback(async () => {
    try {
      const response = await api.get("/api/settings/mux");
      setMuxConfig(response.data);
    } catch (error) {
      console.error("Failed to load Mux config:", error);
    }
  }, []);

  useEffect(() => {
    loadMuxConfig();
  }, [loadMuxConfig]);

  const saveMuxCredentials = async (event) => {
    event.preventDefault();
    if (!muxTokenId.trim() || !muxTokenSecret.trim()) {
      toast.error("Both Token ID and Token Secret are required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/api/settings/mux", { token_id: muxTokenId.trim(), token_secret: muxTokenSecret.trim() });
      setMuxTokenId("");
      setMuxTokenSecret("");
      setStatus(null);
      await loadMuxConfig();
      triggerGlobalRefresh("mux");
      toast.success("Mux credentials saved");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to save Mux credentials");
    } finally {
      setLoading(false);
    }
  };

  // Uses the lightweight status check (one asset) instead of listing the library.
  const testMuxConnection = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/settings/mux/status");
      setStatus(data);
      if (data?.status === "active") toast.success("Mux connection successful");
      else toast.error(data?.message || "Mux connection test failed");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Mux connection test failed");
    } finally {
      setLoading(false);
    }
  };

  const deleteMuxCredentials = async () => {
    if (!window.confirm("Delete Mux credentials? Videos will stop streaming.")) return;
    setLoading(true);
    try {
      await api.delete("/api/settings/mux");
      setMuxConfig(null);
      setStatus(null);
      await loadMuxConfig();
      triggerGlobalRefresh("mux");
      toast.success("Mux credentials deleted");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to delete Mux credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="max-w-3xl space-y-5" data-testid="settings-mux">
      <div className="border-b border-[#FF10F0]/20 pb-4">
        <h2 className="text-lg font-semibold text-white">Mux Video</h2>
        <p className="mt-1 text-sm text-zinc-400">Configure video upload and streaming credentials.</p>
      </div>
      {muxConfig?.configured && (
        <div className="flex items-center gap-2 text-sm text-emerald-300"><CheckCircle2 size={16} />Mux is configured{muxConfig.token_id_preview ? ` (${muxConfig.token_id_preview})` : ""}</div>
      )}
      {status && (
        <p data-testid="settings-mux-status" className={`text-sm ${status.status === "active" ? "text-emerald-300" : "text-amber-300"}`}>{status.message}</p>
      )}
      <form onSubmit={saveMuxCredentials} className="space-y-4">
        <label className="block text-sm text-zinc-300">
          Token ID
          <input value={muxTokenId} onChange={(event) => setMuxTokenId(event.target.value)} autoComplete="off" className="mt-2 w-full rounded border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-[#FF10F0]" />
        </label>
        <label className="block text-sm text-zinc-300">
          Token Secret
          <span className="mt-2 flex rounded border border-zinc-700 bg-zinc-950">
            <input type={showMuxSecret ? "text" : "password"} value={muxTokenSecret} onChange={(event) => setMuxTokenSecret(event.target.value)} autoComplete="new-password" className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm text-zinc-100" />
            <button type="button" aria-label={showMuxSecret ? "Hide token secret" : "Show token secret"} onClick={() => setShowMuxSecret((visible) => !visible)} className="px-3 text-zinc-400 hover:text-white">{showMuxSecret ? <EyeOff size={16} /> : <Eye size={16} />}</button>
          </span>
        </label>
        <div className="flex flex-wrap gap-2">
          <button type="submit" disabled={loading} className="rounded bg-[#FF10F0] px-4 py-2 text-sm font-medium text-black hover:bg-pink-400 disabled:opacity-50">{loading ? "Saving…" : "Save credentials"}</button>
          <button type="button" onClick={testMuxConnection} disabled={loading || !muxConfig?.configured} className="inline-flex items-center gap-2 rounded border border-zinc-700 px-4 py-2 text-sm text-zinc-200 hover:border-[#FF10F0] disabled:opacity-50"><RefreshCw size={14} />Test connection</button>
          {muxConfig?.configured && <button type="button" onClick={deleteMuxCredentials} disabled={loading} className="inline-flex items-center gap-2 rounded border border-red-900 px-4 py-2 text-sm text-red-300 hover:bg-red-950/40 disabled:opacity-50"><Trash2 size={14} />Delete</button>}
        </div>
      </form>
      <a href="https://dashboard.mux.com/settings/access-tokens" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-pink-300 hover:text-pink-200">Mux dashboard <ExternalLink size={13} /></a>
    </section>
  );
}
